/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core';

const maxDrawerWidth = 260;
const minDrawerWidth = 200;

const ChatViewStyles = makeStyles((theme: Theme) =>
	createStyles({
		box: {
			width: '100%',
			padding: theme.spacing(7, 0, 0, 4),
			minWidth: '750px',
		},
		chatBox: {
			display: 'flex',
			width: '100%',
			maxWidth: '1000px',
			minWidth: '650px',
			minHeight: '350px',
			height: '70vh',
			'& .MuiTypography-body1': {
				color: theme.palette.text.primary,
			},
		},
		drawer: {
			width: '25%',
			minWidth: minDrawerWidth,
			maxWidth: maxDrawerWidth,
			flexShrink: 0,
			borderStyle: 'solid',
			borderWidth: 1,
			borderColor: theme.palette.secondary.main,
			borderRadius: '4px 0px 0px 4px',
		},
		drawerPaper: {
			width: '100%',
			position: 'relative!important' as 'relative',
			height: '100%',
			backgroundColor: '#EAEAEF',
			borderWidth: 0,
			borderRadius: '4px 0px 0px 4px',
		},
		listItem: {
			paddingLeft: theme.spacing(3),
			minHeight: theme.spacing(8),
		},
		listItemText: {
			fontSize: '15px',
			fontFamily: 'inherit',
		},
		menuList: {
			'& .Mui-selected': {
				borderRightWidth: '4px',
				borderRightStyle: 'solid',
				borderRightColor: theme.palette.secondary.main,
				fontFamily: theme.typography.fontFamilyBold,
				minHeight: theme.spacing(11),
			},
			padding: 0,
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
		},
		loadingContainer: {
			display: 'flex',
			justifyContent: 'center',
			marginTop: 'auto',
			marginBottom: 'auto',
		},
		loading: {
			color: theme.palette.secondary.light,
		},
		publicIcon: {
			borderRadius: '50%',
			backgroundColor: theme.palette.secondary.main,
			width: '36px',
			height: '36px',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			marginRight: theme.spacing(1),
		},
		image: {
			width: '36px',
			height: '36px',
			marginRight: theme.spacing(1),
			borderRadius: '50%',
			objectFit: 'cover',
		},
		chat: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-end',
			backgroundColor: '#FFFFFF',
			width: '80%',
			maxWidth: '840px',
			minWidth: '584px',
			borderStyle: 'solid',
			borderWidth: 1,
			borderLeftWidth: 0,
			borderColor: '#909091',
			borderRadius: '0px 4px 4px 0px',
		},
		messageBox: {
			height: '100%',
			overflow: 'scroll',
			display: 'flex',
			flexDirection: 'column-reverse',
		},
		daySeparator: {
			fontFamily: theme.typography.fontFamilyBold,
			color: theme.palette.grey['500'] + '!important',
			marginBottom: theme.spacing(3),
			fontSize: '0.9rem',
			marginRight: 'auto',
			marginLeft: 'auto',
			alignText: 'center',
		},
		message: {
			margin: theme.spacing(0, 3, 3, 3),
			display: 'flex',
			'&:last-child': {
				marginTop: theme.spacing(3),
			},
		},
		chatUsername: {
			fontFamily: theme.typography.fontFamilyBold,
			fontSize: '12px',
		},
		chatTextContainer: {
			flexGrow: 1,
			width: '80%',
		},
		chatText: {
			fontSize: '12px',
			wordWrap: 'break-word',
		},
		entryImage: {
			marginTop: theme.spacing(1),
			maxWidth: '160px',
			maxHeight: '200px',
			borderRadius: '8px',
			boxShadow: '0 0 0 0.5px #00000080, 0 1px 3px 0 #0000003D, 0 2px 8px 0 #0000001F',
			cursor: 'pointer',
		},
		timestampContainer: {
			display: 'flex',
			justifyContent: 'flex-end',
			width: '60px',
		},
		timestamp: {
			fontSize: '12px',
		},
		messageButton: {
			padding: 0,
			height: '24px',
			marginTop: theme.spacing(-0.5),
		},
		deleteButton: {
			fontSize: '12px',
		},
		sendMessageContainer: {
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
			display: 'flex',
			marginTop: theme.spacing(0.5),
			marginBottom: theme.spacing(0.5),
		},
		input: {
			flexGrow: 1,
			'& .MuiInput-input': {
				fontSize: '15px',
			},
		},
		sendButton: {
			padding: 0,
			width: '30px',
			height: '30px',
		},
		sendIcon: {
			'-webkit-transform': 'rotate(-45deg)',
			'-moz-transform': 'rotate(-45deg)',
			'-ms-transform': 'rotate(-45deg)',
			'-o-transform': 'rotate(-45deg)',
			transform: 'rotate(-45deg)',
			marginLeft: '5px',
			marginBottom: '2px',
		},
	})
);

export default ChatViewStyles;
